import type { ReactNode } from 'react'

export interface Column<T> {
  key: string
  header: string
  /** 'end' right-aligns and switches to tabular figures. Use for every number. */
  align?: 'start' | 'end'
  sortable?: boolean
  width?: string
  render: (row: T) => ReactNode
}

export type SortDirection = 'ascending' | 'descending'

export interface SortState {
  key: string
  direction: SortDirection
}

export interface DataTableProps<T> {
  caption: string
  columns: Column<T>[]
  rows: T[]
  getRowId: (row: T) => string
  sort?: SortState
  onSortChange?: (next: SortState) => void
  onRowActivate?: (row: T) => void
  selectedId?: string
  loading?: boolean
  emptyState?: ReactNode
  footer?: ReactNode
}

/**
 * DataTable — the most-used component in the system.
 *
 * Real <table> semantics, not a div grid. A grid of divs loses row and column
 * announcement, header association, and the screen reader's table navigation
 * mode entirely — which turns a 40-column employee list into an unstructured
 * wall of text. The ARIA needed to fake it back is more code than the table.
 */
export function DataTable<T>({
  caption,
  columns,
  rows,
  getRowId,
  sort,
  onSortChange,
  onRowActivate,
  selectedId,
  loading = false,
  emptyState,
  footer,
}: DataTableProps<T>) {
  const showEmpty = !loading && rows.length === 0

  function toggleSort(key: string) {
    if (!onSortChange) return
    const direction: SortDirection =
      sort?.key === key && sort.direction === 'ascending' ? 'descending' : 'ascending'
    onSortChange({ key, direction })
  }

  return (
    <div className="lg-table-wrap">
      <div className="lg-table-scroll">
        <table className="lg-table">
          {/* Announced when the table receives focus, so a screen reader user
              knows what they have landed in before hearing 300 cells. */}
          <caption className="lg-sr-only">{caption}</caption>

          <thead>
            <tr>
              {columns.map((col) => {
                const isSorted = sort?.key === col.key
                const ariaSort = isSorted ? sort.direction : undefined
                return (
                  <th
                    key={col.key}
                    scope="col"
                    data-align={col.align ?? 'start'}
                    style={col.width ? { width: col.width } : undefined}
                    aria-sort={col.sortable ? (ariaSort ?? 'none') : undefined}
                  >
                    {col.sortable && onSortChange ? (
                      // A real button inside the th: the header must be
                      // activatable by keyboard, and aria-sort belongs on the
                      // th while the interaction belongs on the control.
                      <button
                        type="button"
                        className="lg-table__sort"
                        aria-sort={ariaSort}
                        onClick={() => toggleSort(col.key)}
                      >
                        {col.header}
                        <span className="lg-table__sort-icon" aria-hidden="true">
                          {isSorted ? (sort.direction === 'ascending' ? '↑' : '↓') : '↕'}
                        </span>
                        <span className="lg-sr-only">
                          {isSorted
                            ? `, sorted ${sort.direction}. Activate to reverse.`
                            : ', not sorted. Activate to sort.'}
                        </span>
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {loading &&
              // Skeleton rows only past a 400ms threshold — see the caller.
              // A shimmer on every load reads as slowness rather than polish.
              Array.from({ length: 8 }, (_, i) => (
                <tr key={`skeleton-${i}`} aria-hidden="true">
                  {columns.map((col) => (
                    <td key={col.key} data-align={col.align ?? 'start'}>
                      <span className="lg-table__skeleton" style={{ width: `${40 + ((i * 7) % 40)}%` }} />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading &&
              rows.map((row) => {
                const id = getRowId(row)
                const interactive = Boolean(onRowActivate)
                return (
                  <tr
                    key={id}
                    data-interactive={interactive || undefined}
                    aria-selected={selectedId === id || undefined}
                    // Click AND Enter. A row that only responds to a mouse is
                    // unreachable for keyboard users, who are most of the
                    // people entering data into this all day.
                    tabIndex={interactive ? 0 : undefined}
                    onClick={interactive ? () => onRowActivate?.(row) : undefined}
                    onKeyDown={
                      interactive
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              onRowActivate?.(row)
                            }
                          }
                        : undefined
                    }
                  >
                    {columns.map((col) => (
                      <td key={col.key} data-align={col.align ?? 'start'}>
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                )
              })}

            {showEmpty && (
              <tr>
                <td colSpan={columns.length}>{emptyState}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {footer && <div className="lg-table__foot">{footer}</div>}

      {/* Announces result-count changes after a filter or a page load, which
          are otherwise silent for a screen reader user. */}
      <div className="lg-sr-only" role="status" aria-live="polite">
        {loading ? 'Loading results' : `${rows.length} result${rows.length === 1 ? '' : 's'}`}
      </div>
    </div>
  )
}
